import React, { useEffect, useState, FormEvent } from "react";
import DatePicker from "react-datepicker";
import { Grid, Form, Button, Alert } from "tabler-react";
import moment from "moment";
import { ContentTextProps, FreeToolsProps } from "../../types";
import TableCard from "../../components/tableCard";
import ServerSidePagination from "../../components/pagination/serverSidePagination";
import { FREE_TOOLS_CHART, FREE_TOOLS, GET_FREE_TOOLS_NAME } from "../../graphql/queries";
import Chart from "../../components/chart";
import { useLazyQuery, useQuery } from "@apollo/react-hooks";
import { getCountArr, isProduction } from "../../helpers";
import "./index.css";

const FreeTools = (props: FreeToolsProps): JSX.Element => {
  const [freeTools, setFreeTools] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [scanLogsPagination, setScanLogsPagination] = useState([]);
  const [filterInput, setFilterInput] = useState("");
  const [filterSocketId, setFilterSocketId] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [total, setTotal] = useState(0);
  const [selectTool, setSelectTool] = useState("");
  const [selectSource, setSelectSource] = useState("");
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const showButtons = localStorage.getItem("_rep");

  const [extensionChart, setExtensionChart] = useState(null as any);
  const [freeToolChart, setFreeToolChart] = useState(null as any);

  const [getCSV, { data, loading }] = useLazyQuery(FREE_TOOLS);
  const [uploadToDrive, { data: driveData, loading: driveDataLoading }] = useLazyQuery(FREE_TOOLS);

  const chartDataQuery = useQuery(FREE_TOOLS_CHART, {
    variables: {
      from: null,
      to: null,
      hideHex: props.hideHex,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const extensionChartQuery = useQuery(FREE_TOOLS_CHART, {
    variables: {
      from: null,
      to: null,
      hideHex: props.hideHex,
      requestSource: "extension",
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  const freeToolChartQuery = useQuery(FREE_TOOLS_CHART, {
    variables: {
      from: null,
      to: null,
      hideHex: props.hideHex,
      requestSource: "free_tools",
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const { data: toolsList } = useQuery(GET_FREE_TOOLS_NAME);
  const tableData = {
    tData:
      freeTools &&
      freeTools.map((item: ContentTextProps) => {
        return {
          "Tool Name": item.toolName,
          "User Email": item.userEmail || "N/A",
          "User ID": item.userId || "N/A",
          "User IP": item.ip,
          "Request Source": item.requestSource ? item.requestSource : "N/A",
          Socket_ID: item.socketId,
          "Created date": moment(new Date(+item.createdAt))
            .utc()
            .format("DD-MM-YYYY hh:mm:ss a"),
          Status: item.status,
          Source: (
            <div title={item.source} className="tool-source">
              {item.source}
            </div>
          ),
        };
      }),
  };

  useEffect(() => {
    if (
      props.data &&
      props.data.FreeTools &&
      props.data.FreeTools.getFreeToolsData &&
      props.data.FreeTools.getFreeToolsData.freeTools
    ) {
      setDataLoading(props.loading);
      setFreeTools(props.data.FreeTools.getFreeToolsData.freeTools);
      setScanLogsPagination(props.data.FreeTools.getFreeToolsData.freeTools);
      setTotal(props.data.FreeTools.getFreeToolsData.count);
    }
  }, [props.data]);

  useEffect(() => {
    if (
      freeToolChartQuery &&
      freeToolChartQuery.data &&
      freeToolChartQuery.data.FreeTools &&
      freeToolChartQuery.data.FreeTools &&
      freeToolChartQuery.data.FreeTools.getFreeToolsTimeSeries
    ) {
      setFreeToolChart(freeToolChartQuery.data.FreeTools.getFreeToolsTimeSeries);
    }

    if (
      extensionChartQuery &&
      extensionChartQuery.data &&
      extensionChartQuery.data.FreeTools &&
      extensionChartQuery.data.FreeTools &&
      extensionChartQuery.data.FreeTools.getFreeToolsTimeSeries
    ) {
      setExtensionChart(extensionChartQuery.data.FreeTools.getFreeToolsTimeSeries);
    }
  }, [freeToolChartQuery, extensionChartQuery]);

  useEffect(() => {
    if (
      driveData &&
      driveData.FreeTools &&
      driveData.FreeTools.getFreeToolsData &&
      driveData.FreeTools.getFreeToolsData.csvURL
    ) {
      window.open(
        isProduction()
          ? `https://api.hexometer.com/v2/app/social-callback/googleDrive?admin=admin&report=freeTools&url=${driveData.FreeTools.getFreeToolsData.csvURL}`
          : `http://localhost:4000/api/app/social-callback/googleDrive?admin=admin&report=freeTools&url=${driveData.FreeTools.getFreeToolsData.csvURL}`,
        "_blank"
      );
    }
  }, [driveData]);

  const handleInputChange = (e: FormEvent) => setFilterInput((e.target as HTMLInputElement).value);
  const handleInputChangeSocket = (e: FormEvent) => setFilterSocketId((e.target as HTMLInputElement).value);
  const handleInputChangeSource = (e: FormEvent) => setFilterSource((e.target as HTMLInputElement).value);
  const handleSelectedTool = (e: FormEvent) => setSelectTool((e.target as HTMLInputElement).value);
  const handleSelectedSource = (e: FormEvent) => setSelectSource((e.target as HTMLInputElement).value);
  const handleSelectedStatus = (e: FormEvent) => setStatus((e.target as HTMLInputElement).value);
  const handleFromDate = (date: Date) => {
    if (date) {
      return setFromDate(moment(date).format("YYYY-MM-DDT00:00:00.000"));
    }
    return setFromDate(null);
  };

  const handleToDate = (date: Date) => {
    if (date) {
      return setToDate(moment(date).format("YYYY-MM-DDT23:59:59.999"));
    }
    return setToDate(null);
  };

  const handleSubmit = (
    event: FormEvent,
    data: string,
    socketId: string,
    source: string,
    toolName: string,
    selectSource: string,
    status: string,
    startDate: string,
    endDate: string
  ) => {
    event.preventDefault();
    props.onFilterDataChange(data.trim());
    props.onFilterBySocketId(socketId.trim());
    props.onFilterBySource(source.trim());
    props.onFilterByStatus(status);
    props.onFromDate(startDate);
    props.onToDate(endDate);

    if (toolName !== "Select tool") {
      props.onToolSelect(toolName);
    } else {
      props.onToolSelect("");
    }
    if (selectSource !== "Select source") {
      props.onSourceSelect(selectSource);
    } else {
      props.onSourceSelect("");
    }
  };

  const handleSortingClick = (column: string) => {
    if (column === "Created date") props.setOrderBy("created_at");
    else if (column === "Tool Name") props.setOrderBy("type");
    if (column === "Click date" || column === "Tool Name") props.setOrderByASC(!props.orderByASC);
  };

  return (
    <Grid>
      <Grid.Row>
        <Grid.Col md={2}>
          <Form.Checkbox
            label="Filter Hexact scans"
            checked={props.hideHex}
            onChange={() => props.setHideHex(!props.hideHex)}
          />
        </Grid.Col>
      </Grid.Row>
      <Grid.Row className="mb-5">
        <div className="col-12 col-md-8">
          {!chartDataQuery.loading &&
            chartDataQuery.data &&
            chartDataQuery.data.FreeTools &&
            chartDataQuery.data.FreeTools.getFreeToolsTimeSeries &&
            !freeToolChartQuery.loading &&
            !extensionChartQuery.loading && (
              <Chart
                yColName="Hexomatic Free tools and extension"
                yColName2="Free tools"
                yColName3="Extension"
                regData={chartDataQuery.data.FreeTools.getFreeToolsTimeSeries}
                regData2={freeToolChart}
                regData3={extensionChart}
                interval={getCountArr(chartDataQuery.data.FreeTools.getFreeToolsTimeSeries.length)}
                chartName="Hexomatic Free tools and extension Activity"
              />
            )}
        </div>
        <div className="col-12 col-md-4">
          <form
            onSubmit={(e) =>
              handleSubmit(
                e,
                filterInput,
                filterSocketId,
                filterSource,
                selectTool,
                selectSource,
                status,
                fromDate,
                toDate
              )
            }
          >
            <Form.Group>
              <Form.Input
                label="Input"
                className="margin-bottom"
                icon="search"
                position="append"
                placeholder="Search for User IP"
                value={filterInput}
                onChange={handleInputChange}
              />
              <Form.Input
                label="Input"
                className="margin-bottom"
                icon="search"
                position="append"
                placeholder="Search for Socket ID"
                value={filterSocketId}
                onChange={handleInputChangeSocket}
              />
              <Form.Input
                label="Input"
                className="margin-bottom"
                icon="search"
                position="append"
                placeholder="Search for Source"
                value={filterSource}
                onChange={handleInputChangeSource}
              />
              <Form.Select label="Tool Name" onChange={handleSelectedTool}>
                <option value="Select tool">Select tool</option>
                {(toolsList && toolsList.FreeTools && toolsList.FreeTools.getFreeToolsNames
                  ? toolsList.FreeTools.getFreeToolsNames
                  : []
                ).map(
                  (tool: any) =>
                    tool &&
                    tool.name && (
                      <option key={tool.name} value={tool.name}>
                        {tool.name}
                      </option>
                    )
                )}
              </Form.Select>
              <Form.Select label="Request source" onChange={handleSelectedSource}>
                <option value="Select source">Select source</option>
                {["free_tools", "extension"].map((tool: any) => (
                  <option key={tool} value={tool}>
                    {tool}
                  </option>
                ))}
              </Form.Select>
              <Form.Select label="Status" onChange={handleSelectedStatus}>
                <option value="Select status">Select status</option>
                {["COMPLETED", "PENDING"].map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Form.Select>
              <Form.Group label="From Date" className="shortcutClicksDatePicker">
                <DatePicker
                  dateFormat="yyyy-MM-dd"
                  selected={fromDate ? new Date(fromDate) : null}
                  onChange={handleFromDate}
                  showMonthDropdown
                  placeholderText="Select a date"
                />
              </Form.Group>
              <Form.Group label="To Date" className="shortcutClicksDatePicker">
                <DatePicker
                  dateFormat="yyyy-MM-dd"
                  selected={toDate ? new Date(toDate) : null}
                  onChange={handleToDate}
                  showMonthDropdown
                  placeholderText="Select a date"
                  minDate={fromDate && new Date(fromDate)}
                />
              </Form.Group>
              <Button type="submit" icon="filter" color="primary">
                Filter
              </Button>
            </Form.Group>
          </form>
        </div>
      </Grid.Row>
      <Grid.Row className={showButtons === "true" ? "justify-content-between" : "justify-content-end"}>
        {showButtons === "true" && (
          <div className="col-12 col-md-3 col-xs-12">
            <Button.List>
              <Button
                onClick={() =>
                  getCSV({
                    variables: {
                      ...props.variables,
                      settings: {
                        csvDownload: true,
                      },
                    },
                  })
                }
                loading={loading}
                color="primary"
                mr={1}
              >
                Get CSV Link
              </Button>
              <Button
                onClick={() =>
                  uploadToDrive({
                    variables: {
                      ...props.variables,
                      settings: {
                        csvDownload: true,
                      },
                    },
                  })
                }
                loading={driveDataLoading}
                color="primary"
              >
                Save to Drive
              </Button>
            </Button.List>
            {!loading &&
              data &&
              data.FreeTools &&
              data.FreeTools.getFreeToolsData &&
              data.FreeTools.getFreeToolsData.csvURL && (
                <Button
                  RootComponent="a"
                  href={data.FreeTools.getFreeToolsData.csvURL}
                  icon="download"
                  className="mt-2"
                >
                  Download CSV
                </Button>
              )}
            <br />
          </div>
        )}
        <Grid.Col md={3}>
          <Alert type="info">Result Count - {total}</Alert>
        </Grid.Col>
      </Grid.Row>
      <Grid.Row cards deck>
        <ServerSidePagination data={scanLogsPagination} total={total} onPageNum={props.onPageNumberChange}>
          <TableCard
            loading={dataLoading}
            data={freeTools}
            tableData={tableData}
            onHeaderClick={handleSortingClick}
          />
        </ServerSidePagination>
      </Grid.Row>
      <Grid.Row className="justify-content-end">
        {total ? (
          <Grid.Col md={3}>
            <Alert type="info">Result Count - {total}</Alert>
          </Grid.Col>
        ) : null}
      </Grid.Row>
    </Grid>
  );
};

export default FreeTools;
